import {MvsCoreService} from "../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {CbEntityCoverage} from "../../model/canvas/cb-coverage-node.interface";
import {CbMetaEntityDto} from "../../model/dto/entity/cb-meta-entity.dto";
import {MvsMessageService} from "../../../../core/shared/service/message/mvs-message.service";

/**
 * CB Entity Documentation Editor Component.
 * Allows editing metadata documentation for an entity.
 * Uses CbMetaEntity service for metadata management.
 */
@Component({
    selector: 'cb-entity-documentation-editor',
    templateUrl: './cb-entity-documentation-editor.component.html',
    styleUrls: ['./cb-entity-documentation-editor.component.scss'],
    standalone: false
})
export class CbEntityDocumentationEditorComponent implements OnInit, OnChanges {

    metaEntityService: MvsCrudService;
    @Input() entity: CbEntityCoverage;

    @Output() saved = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    form: FormGroup;
    loading: boolean = false;
    saving: boolean = false;
    existingMeta: CbMetaEntityDto | null = null;

    constructor(
        protected coreService: MvsCoreService,
        
        private fb: FormBuilder,
        private messageService: MvsMessageService
    ) {
        this.metaEntityService = this.coreService.getCrudService("cb.CbMetaEntity");

        this.initForm();
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entity'] && this.entity) {
            this.loadExistingMeta();
        }
    }

    /**
     * Initialize the form.
     */
    private initForm(): void {
        this.form = this.fb.group({
            description: ['', Validators.required],
            synonyms: ['']
        });
    }

    /**
     * Load existing metadata if available.
     */
    private loadExistingMeta(): void {
        if (!this.entity.documentationId) {
            this.resetForm();
            return;
        }

        this.loading = true;

        this.metaEntityService.get(this.entity.documentationId).subscribe({
            next: (meta) => {
                this.existingMeta = meta as CbMetaEntityDto;
                this.populateForm(meta as CbMetaEntityDto);
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading metadata:', error);
                this.resetForm();
                this.loading = false;
            }
        });
    }

    /**
     * Populate form with existing metadata.
     */
    private populateForm(meta: CbMetaEntityDto): void {
        this.form.patchValue({
            description: meta.description || '',
            synonyms: meta.synonyms || ''
        });
    }

    /**
     * Reset form to empty state.
     */
    private resetForm(): void {
        this.existingMeta = null;
        this.form.reset({
            description: '',
            synonyms: ''
        });
    }

    /**
     * Save the metadata.
     */
    save(): void {
        if (!this.form.valid) {
            this.messageService.showErrorMessage('Error', 'Please fill in required fields');
            return;
        }

        this.saving = true;

        const formValue = this.form.value;

        if (this.existingMeta) {
            // Update existing
            const updated = {
                ...this.existingMeta,
                ...formValue
            };

            this.metaEntityService.update(updated).subscribe({
                next: () => {
                    this.saving = false;
                    this.saved.emit();
                },
                error: (error) => {
                    console.error('Error updating metadata:', error);
                    this.messageService.showErrorMessage('Error', 'Failed to save metadata');
                    this.saving = false;
                }
            });
        } else {
            // Create new
            const newMeta: Partial<CbMetaEntityDto> = {
                ...formValue
            };

            this.metaEntityService.create(newMeta as CbMetaEntityDto).subscribe({
                next: () => {
                    this.saving = false;
                    this.saved.emit();
                },
                error: (error) => {
                    console.error('Error creating metadata:', error);
                    this.messageService.showErrorMessage('Error', 'Failed to save metadata');
                    this.saving = false;
                }
            });
        }
    }

    /**
     * Cancel editing.
     */
    cancel(): void {
        this.cancelled.emit();
    }
}
